import React, { ReactElement } from 'react'

import {
  AvatarStack,
  Box,
  Button,
  Chevron,
  CollapsableCard,
  Icon,
  LargeIcon,
  MediumIcon,
  Tag,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'
import {
  GetApplicationInterviewsQuery,
  AtsInterviewOptionsStatusesEnum,
  AtsInterviewOptionsTypesEnum,
} from 'generated/resources/graphql'

type AtsInterview = NonNullable<
  NonNullable<
    NonNullable<GetApplicationInterviewsQuery['ats']>['interviewsConnection']
  >['nodes']
>[0]
import { sortBy } from 'lodash'
import { useToggle } from 'usehooks-ts'

import TextField from 'modules/core/components/TextField'
import Tooltip from 'modules/core/components/Tooltip'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate, diffBetweenDateTimes, nowBackendDateTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { timezoneToString } from 'modules/core/lib/timezones'
import app from 'modules/core/store/models/app'

import {
  MeetingStartBufferMinutes,
  attendeStatusBadge,
  attendeStatusLabel,
  interviewTypeLabel,
  isInterviewActive,
} from 'modules/ats/lib/utils/interviews'

import { CancelInterviewModal } from '../CancelInterviewModal'

const meetingTypeIcons: Record<AtsInterviewOptionsTypesEnum, LargeIcon | MediumIcon> = {
  online: 'laptop-user',
  in_person: 'building',
  telephone: 'phone',
}

interface Props {
  interview: AtsInterview
}

interface InterviewStatusProps {
  status: AtsInterviewOptionsStatusesEnum
}

const InterviewStatusTag = ({ status }: InterviewStatusProps): ReactElement | null => {
  switch (status) {
    case 'processing':
      return (
        <Tag type="champagne">{i18n.t('ats.interview_status.interview_status.processing')}</Tag>
      )
    case 'completed':
      return <Tag type="lime">{i18n.t('ats.interview_status.interview_status.completed')}</Tag>
    case 'scheduled':
      return <Tag type="champagne">{i18n.t('ats.interview_status.interview_status.scheduled')}</Tag>
    case 'cancelled':
      return <Tag type="wrong">{i18n.t('ats.interview_status.interview_status.cancelled')}</Tag>
    case 'rejected':
      return <Tag type="wrong">{i18n.t('ats.interview_status.interview_status.rejected')}</Tag>
    default:
      return null
  }
}

export const InterviewDetail = ({ interview }: Props) => {
  const [isContentOpen, toggleContentVisibility] = useToggle(false)
  const [isCancelInterviewModalOpen, toggleCancelInterviewModalVisibility] = useToggle(false)
  const chevronDirection = isContentOpen ? 'up' : 'down'
  const handleOnModalClose = () => {
    toggleCancelInterviewModalVisibility()
    toggleContentVisibility()
  }

  if (!interview) return null

  const hasStatus = (status: AtsInterviewOptionsStatusesEnum) => interview.status === status
  const rejected = hasStatus('rejected')
  const canEditInterview = isInterviewActive(interview)

  const attendees = interview.interviewAttendees.map((attendee) => {
    if (attendee.email === interview.candidate.email) {
      return {
        order: 1,
        label: attendeStatusLabel(attendee.status, interview.candidate.fullName),
        url: buildUrl(interview.application.photoThumbnail),
        badge: attendeStatusBadge(attendee.status),
      } as const
    }

    return {
      order: 2,
      label: attendeStatusLabel(attendee.status, attendee.access?.fullName || attendee.email!),
      url: buildUrl(attendee.access?.avatar),
      badge: attendeStatusBadge(attendee.status),
    } as const
  })

  const hasActiveConferencing = hasStatus('scheduled') && interview.url
  const minutesForMeetingToStart =
    diffBetweenDateTimes(interview.startsAt, nowBackendDateTime()) / 60
  const canJoin = hasActiveConferencing && minutesForMeetingToStart <= MeetingStartBufferMinutes

  const copyText = (text: string) => (event: React.SyntheticEvent<Element, Event>) => {
    event.preventDefault()
    text && copy(text)
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.copied'),
    })
  }

  return (
    <CollapsableCard
      open={isContentOpen}
      Card={
        <Box gap="s16" padding="s8">
          <Box flexDirection="row" alignItems="center">
            <Box flexDirection="row" alignItems="center" gap="s8" flexGrow>
              <Tooltip.Alt
                attachment="top center"
                label={interviewTypeLabel(interview.interviewType)}
              >
                <Box width="s24" height="s24">
                  {interview.interviewType !== 'telephone' ? (
                    <Icon.Large
                      color="grey700"
                      icon={meetingTypeIcons[interview.interviewType] as LargeIcon}
                    />
                  ) : (
                    <Icon.Medium
                      color="grey700"
                      icon={meetingTypeIcons[interview.interviewType] as MediumIcon}
                    />
                  )}
                </Box>
              </Tooltip.Alt>
              <Text.Normal weight="medium">
                {convertDate(interview.startsAt, 'backend_datetime', 'ui_long_with_year')}
              </Text.Normal>
            </Box>
            <Touchable onClick={() => canEditInterview && toggleContentVisibility()}>
              <Box gap="s8" flexDirection="row">
                <InterviewStatusTag status={interview.status} />
                {canEditInterview && (
                  <Chevron size="big" type="terciary" direction={chevronDirection} />
                )}
              </Box>
            </Touchable>
          </Box>
          <Box flexDirection="row" gap="s8">
            <Text.Normal size="200">
              {convertDate(interview.startsAt, 'backend_datetime', 'ui_time')}
            </Text.Normal>
            <Text.Normal size="200">
              {i18n.t('ats.interview_status.duration_minutes', { minutes: interview.duration })}・
              {timezoneToString(interview.timezone!)}・{interview.applicationPhase.name}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            <AvatarStack
              size="s40"
              gap="s24"
              leftover={0}
              backgroundColor="grey000White"
              textSize="200"
              textColor="grey1000"
              avatars={sortBy(attendees, (attendee) => attendee.order)}
            />
            {!isContentOpen && canJoin && (
              <Button.Router to={interview.url!} blankTarget type="secondary" iconName="play">
                {i18n.t('ats.interview_status.join_meeting')}
              </Button.Router>
            )}
          </Box>
          {rejected && (
            <Box gap="s2" flexDirection="row" alignItems="center">
              <Icon.Medium icon="alert-circle" color="radicalRed1500" />
              <Text.Normal color="radicalRed1500" size="200">
                {i18n.t('ats.interview_status.rejected')}
              </Text.Normal>
            </Box>
          )}
          {isContentOpen && hasActiveConferencing && (
            <Box gap="s4">
              <Text.Normal size="200">{i18n.t('ats.interview_status.meeting_link')}</Text.Normal>
              <Box flexDirection="row" gap="s16" alignItems="center">
                <TextField value={interview.url!} readonly />
                <Button onClick={copyText(interview.url!)} type="secondary" iconName="link">
                  {i18n.t('ats.interview_status.copy_link')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      }
      Content={
        <Box alignItems="flexEnd">
          <Button type="negative" onClick={toggleCancelInterviewModalVisibility}>
            {i18n.t('ats.schedule_interview.cancel_interview')}
          </Button>
          {isCancelInterviewModalOpen && (
            <CancelInterviewModal interviewId={interview.id!} onClose={handleOnModalClose} />
          )}
        </Box>
      }
    />
  )
}
